<template>
  <b-modal
    modal-class="component-modal-lab-product"
    id="LabProductItem"
    ref="LabProductItem"
    size="lg"
    title="รายการแลป"
    hide-footer
    no-close-on-backdrop
    @hidden="setDefaultValue"
  >
    <b-form @submit.prevent="saveLabProduct">
      <div :class="{ 'disabled-lab-form': !canWrite }">
      <b-form-group label-cols="3" label="รหัส:" label-align="right">
        <b-form-input
          v-model="$v.labProduct.code.$model"
          class="required"
          :class="{ 'is-invalid': $v.labProduct.code.$error }"
        ></b-form-input>
      </b-form-group>
      <b-form-group label-cols="3" label="ชื่อรายการ(ไทย):" label-align="right">
        <b-form-input
          v-model="$v.labProduct.nameTh.$model"
          class="required"
          :class="{ 'is-invalid': $v.labProduct.nameTh.$error }"
        >
        </b-form-input>
      </b-form-group>
      <b-form-group label-cols="3" label="ชื่อรายการ(Eng):" label-align="right">
        <b-form-input v-model="labProduct.nameEn"></b-form-input>
      </b-form-group>
      <b-form-group label-cols="3" label="บริษัทแลป:" label-align="right">
        <b-row>
          <b-col cols="10">
            <b-form-select
              v-model="labProduct.companyLabId"
              :options="getLabCompanyList"
              value-field="id"
              text-field="nameTh"
            >
              <template #first>
                <b-form-select-option :value="null"
                  >-- ไม่ระบุ --</b-form-select-option
                >
              </template></b-form-select
            ></b-col
          >
          <b-col cols="2">
            <b-button
              v-if="!clinicMode && $canManageMasterData()"
              block
              @click="openLabCompanyModal"
            >
              <i class="fas fa-edit"></i>
            </b-button>
          </b-col>
        </b-row>
      </b-form-group>

      <b-form-group
        class="mt-3"
        label-cols="3"
        label="หัก DF:"
        label-align="right"
      >
        <b-row>
          <b-input-group class="col">
            <b-form-input
              v-model="$v.labProduct.df.$model"
              number
              class="required"
              :class="{ 'is-invalid': $v.labProduct.df.$error }"
            ></b-form-input>
            <template v-slot:append>
              <b-input-group-text>{{
                labProduct.dfType == 0 ? "%" : "บาท"
              }}</b-input-group-text>
            </template>
          </b-input-group>
          <b-form-radio-group
            class="align-self-center"
            v-model="labProduct.dfType"
          >
            <b-form-radio :value="0">Percent</b-form-radio>
            <b-form-radio :value="1">Fixed</b-form-radio>
          </b-form-radio-group>
        </b-row>
      </b-form-group>

      <b-form-group label-cols="3" label="กลุ่มภาษี VAT:" label-align="right">
        <b-form-select v-model="labProduct.vatProdPostingGroup">
          <template #first>
            <b-form-select-option :value="null">-- ไม่ระบุ --</b-form-select-option>
          </template>
          <b-form-select-option value="GVAT">GVAT - General VAT</b-form-select-option>
          <b-form-select-option value="SVAT">SVAT - Service VAT</b-form-select-option>
          <b-form-select-option value="NOVAT">NOVAT - No VAT</b-form-select-option>
          <b-form-select-option value="VAT0">VAT0 - Zero VAT</b-form-select-option>
          <b-form-select-option value="UVAT">UVAT - Unspecified VAT</b-form-select-option>
        </b-form-select>
      </b-form-group>

      </div>

      <template v-if="clinicMode">
        <hr />
        <CostGlFields
          v-model="gl"
          :cost-accounts="costAccounts"
          :has-revenue-gl="false"
          :current-cost="glCurrent.costGl"
          :disabled="!canAssignGl"
          :required="glOnly"
          :submitted="glSubmitted"
        />
      </template>

      <div :class="{ 'hidden-lab-form-btn': !canSubmit }">
        <hr />
        <b-row class="d-flex flex-row-reverse">
          <div>
            <b-button class="ml-2 mr-3" type="submit" variant="primary">{{
              glOnly || labProduct.id ? "บันทึกรายการ" : "สร้างรายการ"
            }}</b-button>
          </div>
        </b-row>
      </div>
    </b-form>
    <LabCompanyModal ref="LabCompanyModal"></LabCompanyModal>
    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </b-modal>
</template>

<script>
import Dialog from "@/components/modal/Dialog";
import LabCompanyModal from "@/components/modal/LabCompany";
import Loading from "@/components/Loading";
import CostGlFields from "@/components/CostGlFields.vue";
import { codeRequired } from "@/utils/masterCode";
import clinicGlMixin from "@/mixins/clinicGlMixin";
import pick from "lodash/pick";
import { mapActions, mapGetters } from "vuex";
import "@/theme/modal/AptItem.scss";
import {
  required,
  requiredIf,
  numeric,
  minLength,
  between,
  minValue,
  sameAs,
  not,
  helpers,
  email,
} from "vuelidate/lib/validators";

const CLINIC_LAB_FIELDS = [
  "id",
  "code",
  "nameTh",
  "nameEn",
  "companyLabId",
  "df",
  "dfType",
  "vatProdPostingGroup",
];

export default {
  name: "labProductItem",
  components: {
    Dialog,
    Loading,
    LabCompanyModal,
    CostGlFields,
  },
  mixins: [clinicGlMixin],
  props: {
    // PROCUREMENT edits the clinic-level master (no branchUrl): reads and writes go
    // to the clinic endpoints and the Cost G/L section is shown.
    clinicMode: { type: Boolean, default: false },
  },
  data() {
    return {
      isLoading: false,
      labProduct: {
        id: null,
        code: "",
        nameTh: "",
        nameEn: "",
        companyLabId: null,
        dfType: 0, //คำนวนเป็น %
        df: 50, //50 %
        vatProdPostingGroup: null,
        // favourite: false,
        // creationDt: null,
      },
    };
  },
  validations: {
    labProduct: {
      code: { codeRequired },
      nameTh: { required },
      df: {
        required,
        // between: () => {
        //   if (this.labProduct.dfType === 0) {
        //     return between(0, 100);
        //   }
        // },
        // minValue: () => {
        //   if (this.labProduct.dfType === 1) {
        //     return minValue(0);
        //   }
        // },
      },
    },
  },
  computed: {
    // which master the clinic G/L section assigns to
    kind() {
      return "lab";
    },
    // writes are PROCUREMENT's, at clinic level; the branch endpoints are closed
    canWrite() {
      return this.clinicMode
        ? this.$can("master_lab.write")
        : this.$can("master_lab.write.branch");
    },
    ...mapGetters({
      getLabCompanyList: "moduleLab/getLabCompanyList",
      getUserList: "moduleUser/getUserList",
      getUserListBy: "moduleUser/getUserListBy",
    }),
  },
  watch: {},

  methods: {
    ...mapActions({
      fetchLabCompanyList: "moduleLab/fetchLabCompanyList",
      fetchLabProductList: "moduleLab/fetchLabProductList",
      findLabProductById: "moduleLab/findLabProductById",
    }),
    init() {},
    // One entry point for the master reads / writes: the branch store for the
    // branch page, the clinic store (no branchUrl) in clinic mode.
    masterRequest(action, event) {
      const clinicUrl = this.$route.params.clinicUrl;
      if (!this.clinicMode) {
        return this.$store.dispatch(`moduleLab/${action}`, {
          event,
          clinicUrl,
          branchUrl: this.$route.params.branchUrl,
        });
      }
      const clinicAction = {
        createLabProduct: "create",
        updateLabProduct: "update",
      }[action];
      return this.$store.dispatch(`moduleClinicMaster/${clinicAction}`, {
        kind: "lab",
        clinicUrl,
        event,
        id: event.id,
      });
    },
    async show(item) {
      if (this.clinicMode) return this.showClinic(item);
      this.$bvModal.show("LabProductItem");

      if (item) {
        this.labProduct = { ...item };
        if (!this.getLabCompanyList.find((i) => i.id == item.companyLabId)) {
          this.labProduct.companyLabId = null;
        }
      }
    },
    // Read the lab fresh (the list row is only a summary), then open the modal
    async showClinic(item) {
      if (!item) {
        this.resetGl();
        this.$bvModal.show("LabProductItem");
        return;
      }
      this.isLoading = true;
      // the modal is not open yet, so tell the page to show its spinner
      this.$emit("loading", true);
      try {
        const res = await this.$store.dispatch("moduleClinicMaster/fetchOne", {
          kind: "lab",
          clinicUrl: this.$route.params.clinicUrl,
          id: item.id,
        });
        this.labProduct = { ...this.labProduct, ...res.data };
        if (!this.getLabCompanyList.find((i) => i.id == res.data.companyLabId)) {
          this.labProduct.companyLabId = null;
        }
        this.loadGl(res.data, item);
        this.$bvModal.show("LabProductItem");
      } catch (err) {
        console.error(err);
        this.$parent.showDialogToast("danger", "โหลดข้อมูลรายการแลปไม่สำเร็จ");
      } finally {
        this.isLoading = false;
        this.$emit("loading", false);
      }
    },
    saveLabProduct() {
      // ACCOUNTANT without the master permission: only the Cost G/L is saved
      if (this.glOnly) {
        return this.saveGlOnly(this.labProduct.id, this.labProduct.nameTh, () => {
          this.$bvModal.hide("LabProductItem");
          this.setDefaultValue();
        });
      }
      // The form is only visually disabled and Enter still submits: re-check.
      if (!this.canWrite) {
        this.$refs.Dialog.showToast("danger", "คุณไม่มีสิทธิ์แก้ไขข้อมูลรายการแลป");
        return;
      }
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.isLoading = true;
      const without = (object, keys) =>
        keys.reduce((o, k) => {
          const { [k]: _, ...p } = o;
          return p;
        }, object);

      let event = without(this.labProduct, [
        "favorite",
        "branchId",
        "clinicId",
        "creationUid",
        "creationDt",
        "modifyUid",
        "modifyDt",
        "del",
      ]);
      if (this.clinicMode) {
        // the clinic endpoints accept the master fields only: status and the Cost G/L
        // belong to the accountant and are assigned separately
        event = pick(event, CLINIC_LAB_FIELDS);
      }
      if (!this.labProduct?.id) {
        delete event.id;
        delete event.favourite;
        this.masterRequest("createLabProduct", event)
          .then(async (res) => {
            if (res.status == "200") {
              const gl = await this.assignGlAfterSave(res.data && res.data.id);
              this.notifySaved(`สร้างรายการ "${this.labProduct.nameTh}" สำเร็จ`, undefined, gl);
            }
          })
          .then(() => {
            this.refreshList();
            this.$bvModal.hide("LabProductItem");
          })
          .catch(err => {
            // the global handler already toasted the reason (e.g. a duplicate code):
            // the modal stays open so the entry can be corrected
            console.error(err);
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.masterRequest("updateLabProduct", event)
          .then(async (res) => {
            const gl = await this.assignGlAfterSave(this.labProduct.id);
            this.notifySaved(
              `แก้ไขรายการ "${this.labProduct.nameTh}" สำเร็จ`,
              res && res.data && res.data.erpSync,
              gl
            );
          })
          .then(() => {
            this.refreshList();
            this.$bvModal.hide("LabProductItem");
          })
          .catch(err => {
            // the global handler already toasted the reason (e.g. a duplicate code):
            // the modal stays open so the entry can be corrected
            console.error(err);
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    setDefaultValue() {
      this.resetGl();
      this.labProduct = Object.assign(
        {},
        {
          id: null,
          code: "",
          nameTh: "",
          nameEn: "",
          companyLabId: null,
          dfType: 0, //คำนวนเป็น %
          df: 50, //50 %
          vatProdPostingGroup: null,
          favourite: false,
          // creationDt: null,
        }
      );
      this.$v.$reset();
    },
    openLabCompanyModal() {
      this.$refs.LabCompanyModal.show();
    },
    refreshList() {
      // the clinic page reloads its own list
      if (this.clinicMode) {
        this.$emit("saved");
        return;
      }
      let clinicUrl = this.$route.params.clinicUrl;
      let branchUrl = this.$route.params.branchUrl;

      this.fetchLabProductList({
        clinicUrl,
        branchUrl,
      });
    },
  },
};
</script>

<style>
.required {
  background-color: #ccecff6c;
}
.disabled-lab-form {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
}
.hidden-lab-form-btn {
  display: none;
}
</style>